<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <router-link to="/app/syllabus/list" class="btn btn-outline-secondary btn-sm">
          <vue-feather type="arrow-left" size="14" class="me-1" />
          Back
        </router-link>
      </template>
    </breadcrumb-index>

    <div class="card">
      <div class="card-header">
        <h6 class="card-title mb-0">Edit Syllabus</h6>
      </div>
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <div class="row">
            <div class="col-lg-8">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Title <span class="text-danger">*</span></label>
                  <input v-model="form.title" type="text" class="form-control" required />
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Course <span class="text-danger">*</span></label>
                  <select v-model="form.courseId" class="form-select" required>
                    <option value="">Select Course</option>
                    <option v-for="course in courses" :key="course.id" :value="course.id">{{ course.name }}</option>
                  </select>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Description</label>
                <textarea v-model="form.description" class="form-control" rows="2"></textarea>
              </div>

              <div class="mb-3">
                <label class="form-label">Topics</label>
                <div v-for="(topic, index) in form.topics" :key="index" class="input-group mb-2">
                  <span class="input-group-text">{{ index + 1 }}</span>
                  <input v-model="topic.title" type="text" class="form-control" placeholder="Topic title" />
                  <button type="button" class="btn btn-outline-danger" @click="removeTopic(index)" v-if="form.topics.length > 1">
                    <vue-feather type="x" size="14" />
                  </button>
                </div>
                <button type="button" class="btn btn-outline-secondary btn-sm" @click="addTopic">
                  <vue-feather type="plus" size="14" class="me-1" />
                  Add Topic
                </button>
              </div>

              <div class="mb-3">
                <label class="form-label">Status</label>
                <select v-model="form.status" class="form-select">
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>

              <div v-if="error" class="alert alert-danger">{{ error }}</div>

              <div class="d-flex gap-2 pt-2">
                <button type="submit" class="btn btn-primary" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
                  Update Syllabus
                </button>
                <router-link to="/app/syllabus/list" class="btn btn-outline-secondary">Cancel</router-link>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'

export default {
  name: 'EditSyllabus',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const alert = useAlert()
    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Syllabus', url: '/app/syllabus/list' },
      { title: 'Edit Syllabus', active: true }
    ]

    const form = ref({ title: '', courseId: '', description: '', status: 'draft', topics: [{ title: '' }] })
    const courses = ref([])
    const loading = ref(false)
    const error = ref('')

    const fetchData = async () => {
      try {
        const [syllabusRes, coursesRes] = await Promise.all([
          api.get(`/v1/admin/syllabus/${route.params.id}`),
          api.get('/v1/admin/courses')
        ])
        form.value = syllabusRes.data.syllabus
        courses.value = coursesRes.data.courses
      } catch (err) {
        alert.error('Error', 'Syllabus not found')
        router.push('/app/syllabus/list')
      }
    }

    const addTopic = () => form.value.topics.push({ title: '' })
    const removeTopic = (index) => form.value.topics.splice(index, 1)

    const handleSubmit = async () => {
      loading.value = true
      error.value = ''

      try {
        await api.put(`/v1/admin/syllabus/${route.params.id}`, form.value)
        alert.success('Success', 'Syllabus updated successfully')
        router.push('/app/syllabus/list')
      } catch (err) {
        error.value = err.response?.data?.message || 'Failed to update syllabus'
      } finally {
        loading.value = false
      }
    }

    onMounted(fetchData)

    return { breadcrumbs, form, courses, loading, error, addTopic, removeTopic, handleSubmit }
  }
}
</script>
